import { ChangeDetectorRef, Component, DestroyRef, inject, Inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { faArrowRight, faBarcode, faFileLines, faLocationDot, faTrash, faWarehouse, faXmark } from '@fortawesome/free-solid-svg-icons';
import { NbToastrService } from '@nebular/theme';
import { forkJoin } from 'rxjs';
import { AuthService } from '../../../../../../@core/auth/auth.service';
import { ErrorService } from '../../../../../../@core/utils/error.service';
import { ConfirmationDialogComponent } from '../../../../../../partials/content/widgets/general/confirmation-dialog/confirmation-dialog.component';
import { RoomService } from '../../../../../core/services/storage/room-service';
import { WarehouseService } from '../../../../../core/services/storage/warehouse-service';
import { TransactionService } from '../../../../../core/services/TransactionService';
import { InventoryService } from '../../../../pick-plan/services/inventory.service';
import { HandlingUnitBulkDelete, HandlingUnitBulkMove } from '../../../models/handling-unit-bulk-actions';
import { HandlingUnitMasterDTO, handlingUnitLocation } from '../../../models/handling-unit-master';
import { InventorySessionService } from '../../../services/inventory-session.service';
import { InventoryScanPopupComponent } from '../inventory-scan-popup/inventory-scan-popup.component';
import { RemoveInventoryConfirmationComponent } from '../inventory-summary-popup/remove-inventory-confirmation/remove-inventory-confirmation.component';

/** A single unit, or (isMultiple) the bulk-move selection. */
export interface InventoryMoveData {
  hum: HandlingUnitMasterDTO & HandlingUnitBulkMove;
}

// Tenants that see the contamination checklist for reason code 1.
const CONTAMINATION_CHECK_TENANTS = ['b0ed668d-7ef2-4a23-a333-94ad278f45d7', 'e7e73238-662f-4da2-b3a5-89f4abb87969'];

@Component({
  selector: 'inventory-move-location-popup',
  templateUrl: './inventory-move-location-popup.component.html',
  styleUrls: ['./inventory-move-location-popup.component.scss']
})
export class InventoryMoveLocationPopupComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  public isLoading: boolean = false;
  public faXmark = faXmark;
  public faTrash = faTrash;
  public faBarcode = faBarcode;
  public faLocationDot = faLocationDot;
  public faWarehouse = faWarehouse;
  public faFileLines = faFileLines;
  public faArrowRight = faArrowRight;
  public warehouses = [];
  public rooms = [];
  public locations = [];
  public allWarehouses = [];
  public allRooms = [];
  public allLocations = [];
  public transactionReasons = [];
  
  public moveToHum: HandlingUnitMasterDTO = {};
  bulkRemoveInventory: HandlingUnitBulkDelete = {};

  static open(dialog: MatDialog, data: InventoryMoveData): MatDialogRef<InventoryMoveLocationPopupComponent, unknown> {
    return dialog.open(InventoryMoveLocationPopupComponent, { data, width: '860px', maxWidth: '95vw' });
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: InventoryMoveData,
    public dialogRef: MatDialogRef<InventoryMoveLocationPopupComponent, unknown>,
    private errorService: ErrorService,
    public roomService: RoomService,
    public warehouseService: WarehouseService,
    public transactionService: TransactionService,
    private changeDetRef: ChangeDetectorRef,
    public authService: AuthService,
    private dialog: MatDialog,
    private toastr: NbToastrService,
    private inventoryService: InventoryService,
    private session: InventorySessionService,
    ) { }

  ngOnInit() {
    this.isLoading = true;
    forkJoin([
      this.warehouseService.getWarehouses(),
      this.roomService.getRooms(),
      this.roomService.getRoomLocations(),
      this.transactionService.getTransactionReasonCodes()
      //this.roomService.getRooms()
    ])
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({next:x => {
        this.allWarehouses = x[0];
        this.allRooms = x[1];
        this.allLocations = x[2];
        this.transactionReasons = x[3];

        this.warehouses = this.allWarehouses;
        this.rooms = this.allRooms;
        this.locations = this.allLocations; 
        
        if(this.data.hum.isMultiple) {

          this.moveToHum = JSON.parse(JSON.stringify(this.data.hum));
          
          // this.moveToHum.handlingUnitDetails.forEach(x => { x.isChecked = true; });
          this.isLoading = false;
          if(this.moveToHum.roomLocationId) 
            this.locationSelectionChanged()
            
          this.changeDetRef.detectChanges();

        } else if(!this.data.hum.isMultiple) {

          this.moveToHum = JSON.parse(JSON.stringify(this.data.hum));
          this.moveToHum.handlingUnitDetails.forEach(x => { x.isChecked = true; });

          if(this.moveToHum.roomLocationId)
            this.locationSelectionChanged()
          this.changeDetRef.detectChanges();
          }
        
          this.isLoading = false;
      },error: err => {
        this.errorService.handleError(err);
        this.isLoading = false;
      }});
  }


  get subtitle(): string {
    const hum = this.data.hum;
    if (hum.isMultiple)
      return `${hum.handlingUnitMasterIds?.length ?? 0} handling units`;
    const count = hum.handlingUnitDetails?.length ?? 0;
    return [hum.barcode, `${count} ${count === 1 ? 'item' : 'items'}`].filter(Boolean).join(' · ');
  }

  get currentLocation(): string {
    return this.data.hum.isMultiple ? '' : handlingUnitLocation(this.data.hum);
  }

  get showContaminationChecks(): boolean {
    return this.moveToHum.transactionReasonCodeId == 1
      && CONTAMINATION_CHECK_TENANTS.includes(this.authService.getCurrentUser()?.tenant?.tenantId);
  }

  close() {
    this.dialogRef.close();
  }

  removeInventory() {
    if(!this.moveToHum.transactionReasonCodeId || this.moveToHum.transactionReasonCodeId == 0){
      this.toastr.warning("Please select a reason code", "No reason code");
      return;
    }

    this.dialog.open(ConfirmationDialogComponent, {
      minWidth: '10%',
      data: {
        confirmLabel: 'Yes',
        title: 'Remove Inventory',
        confirmMessage: 'Are you sure you want to delete the Inventory for ' + this.moveToHum.barcode + '?',
        cancelLabel: 'No'
      }
    }).afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(result => {
      if (result !== true)
        return;

      this.isLoading = true;
      this.inventoryService.deleteInventoryHUM(this.moveToHum.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({next: () => {
        this.dialogRef.close(true);
      }, error: err => {
        this.errorService.handleError(err);
        this.isLoading = false;
      }});
    });
  }

  scan() {
    const dialogRef = this.dialog.open(InventoryScanPopupComponent, {
      minWidth: '30%',
      data: {
        isHumEdit: true
      }
    });

    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(x => {
      if (!x)
        return;

      if (x.isLocation) {
        this.moveToHum.roomLocationId = x.location.id;
        this.locationSelectionChanged();
      }

      if (x.isRoom) {
        this.moveToHum.roomId = x.room.id;
        this.roomSelectionChanged();
      }

      if (x.isWarehouse) {
        this.moveToHum.warehouseId = x.warehouse.id;
        this.warehouseSelectionChanged();
      }

      this.isLoading = false;
      this.changeDetRef.markForCheck();
    });
  }

  submit() {
    this.isLoading = true;
    let moveAll = true;

    this.moveToHum.handlingUnitDetails.forEach(x => {
      if (!x.isChecked) {
        moveAll = false;
      }
    });

    if (moveAll) {
      this.data.hum.warehouseId = this.moveToHum.warehouseId;
      this.data.hum.warehouseName = this.allWarehouses.find(x => x.id == this.moveToHum.warehouseId).name;
      this.data.hum.roomId = this.moveToHum.roomId;      

      let moveToRoom = this.allRooms.find(x => x.id == this.moveToHum.roomId);

      if(moveToRoom)
        this.data.hum.roomName = moveToRoom.name;
        
      this.data.hum.roomLocationId = this.moveToHum.roomLocationId;
      this.data.hum.transactionReasonCodeId = this.moveToHum.transactionReasonCodeId;
      if(this.allLocations.find(x => x.id == this.moveToHum.roomLocationId))
        this.data.hum.roomLocationName = this.allLocations.find(x => x.id == this.moveToHum.roomLocationId).name;
      else
        this.data.hum.roomLocationName = null;

      this.inventoryService.putInventoryHUM(this.data.hum).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({next:x => {
        this.dialogRef.close(this.data.hum);
      }, error:err => {
        this.errorService.handleError(err);
        this.isLoading = false;
      }});
    }
    else {
      this.data.hum.transactionReasonCodeId = this.moveToHum.transactionReasonCodeId;
      this.data.hum.handlingUnitDetails.forEach(x => {
        let currentDetail = this.moveToHum.handlingUnitDetails.find(mx => mx.id == x.id);
        if (currentDetail.isChecked) {
          if (currentDetail.quantity == x.quantity) {
            x.isActive = false;
          }
          else if (currentDetail.quantity != x.quantity) {
            x.quantity = x.quantity - currentDetail.quantity
          }
        }
        else {
          this.moveToHum.handlingUnitDetails = this.moveToHum.handlingUnitDetails.filter(x => x.id != currentDetail.id);
        }
        currentDetail.id = 0;
      });

      this.moveToHum.id = 0;

      forkJoin([
        this.inventoryService.putInventoryHUM(this.data.hum),
        this.inventoryService.postInventoryHUM(this.moveToHum)
        //this.roomService.getRooms()
      ]).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({next:x => {
        this.dialogRef.close(this.data.hum);
      },error: err => {
        this.errorService.handleError(err);
        this.isLoading = false;
        this.changeDetRef.detectChanges();
      }});
    }
  }

  removeInventoryBulk() {
    const count = this.session.handlingUnitBulkMoveList[0]?.handlingUnitMasterIds?.length ?? 0;
    RemoveInventoryConfirmationComponent.open(this.dialog, { label: `${count} handling ${count === 1 ? 'unit' : 'units'}` })
      .afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(result => {

      if (result?.result == true) {

        this.isLoading = true;

        this.bulkRemoveInventory.handlingUnitMasterIds = this.session.handlingUnitBulkMoveList[0].handlingUnitMasterIds;
        this.bulkRemoveInventory.reasonCodeId = 0;  

        if (result.transactionReasonCodeId) {
          this.bulkRemoveInventory.reasonCodeId = result.transactionReasonCodeId;
        }

        this.inventoryService.bulkDeleteHandlingUnitMasters(this.bulkRemoveInventory).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({next:x => {
          this.toastr.success('Items successfully removed from inventory', 'SUCCESS');
          this.dialogRef.close(true);
          this.session.handlingUnitBulkMoveList = [];
        },error: err => {
          this.isLoading = false;
          this.errorService.handleError(err);
        }});
      }
    });
  }

  saveBulkMove() {
    this.isLoading = true;
    this.inventoryService.bulkMoveHandlingUnitMasters(this.moveToHum).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({next:x => {
      this.isLoading = false;
      this.dialogRef.close(true);
      this.toastr.success('Bulk move successful', 'SUCCESS');
      this.changeDetRef.detectChanges();
    },error: err => {
      this.isLoading = false;
      this.errorService.handleError(err);
    }});
    
  }

  roomSelectionChanged(){
    let room = this.allRooms.find(x => x.id == this.moveToHum.roomId);
    this.moveToHum.warehouseId = room.warehouseId;
    this.locations = [...this.allLocations.filter(x => {
      return this.rooms.some( f => {
        return room.id == x.roomId;
      });
    })];
    this.moveToHum.roomLocationId = null;
    this.changeDetRef.detectChanges();
  }

  warehouseSelectionChanged(){
    let warehouse = this.allWarehouses.find(x => x.id == this.moveToHum.warehouseId);
    this.rooms = [...this.allRooms.filter(x => x.warehouseId == warehouse.id)];
    this.locations = [...this.allLocations.filter(x => {
      return this.rooms.some( f => {
        return f.id == x.roomId;
      });
    })];
    this.moveToHum.roomId = null;
    this.moveToHum.roomLocationId = null;
    this.changeDetRef.detectChanges();
  }

  locationSelectionChanged(){
    let location = this.allLocations.find(x => x.id == this.moveToHum.roomLocationId);
    let room = this.allRooms.find(x => x.id == location.roomId);
    this.moveToHum.warehouseId = room.warehouseId;
    this.moveToHum.roomId = room.id;
    this.locations = [...this.allLocations.filter(x => {
      return this.rooms.some( f => {
        return room.id == x.roomId;
      });
    })];
    //this.moveToHum.roomLocationId = null;
    this.changeDetRef.detectChanges();
  }

  setReasonId(){
    let t = this.moveToHum;
    
  }
}
